import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import { Api } from '../../../api';
import { daySegmentColor, dayState, toDaySlots } from '../status';
import UptimeTooltip from './UptimeTooltip';

type Day = Api.CompanyUptime.V['resources'][number]['statusHistory'][number];

type Props = {
  statusHistory: ReadonlyArray<Day>;
  /** Number of day slots to render, e.g. 90 desktop / 30 mobile. */
  days: number;
  /** Accessible summary for the whole bar, e.g. "API uptime, last 90 days". */
  label: string;
};

type Hover = {
  day: Day;
  index: number;
  anchorX: number;
  viewportTop: number;
  containerWidth: number;
};

const OPEN_DELAY_MS = 100;

export default function UptimeBar({ statusHistory, days, label }: Props) {
  const barRef = React.useRef<HTMLDivElement>(null);
  const openTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hover, setHover] = React.useState<Hover | null>(null);

  React.useEffect(
    () => () => {
      if (openTimer.current) clearTimeout(openTimer.current);
    },
    []
  );

  function show(day: Day, index: number, el: HTMLDivElement) {
    const bar = barRef.current;
    if (!bar) return;
    const barRect = bar.getBoundingClientRect();
    const cellRect = el.getBoundingClientRect();
    const next: Hover = {
      day,
      index,
      anchorX: cellRect.left - barRect.left + cellRect.width / 2,
      viewportTop: barRect.top,
      containerWidth: barRect.width,
    };
    // Once open the tooltip tracks the pointer to the next cell immediately;
    // the delay only applies to the initial open.
    if (hover) {
      setHover(next);
      return;
    }
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => setHover(next), OPEN_DELAY_MS);
  }

  function hide() {
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = null;
    setHover(null);
  }

  return (
    <html.div
      ref={barRef}
      style={styles.bar}
      role="img"
      aria-label={label}
      onPointerLeave={hide}
    >
      {toDaySlots(statusHistory, days).map((day, index) => {
        if (!day) {
          return (
            <html.div
              key={`empty-${index}`}
              style={styles.segment}
              aria-hidden
            />
          );
        }
        const segmentColor = daySegmentColor(day);
        return (
          <html.div
            key={day.day}
            style={[
              styles.segment,
              segmentColor !== null && styles.segmentColor(segmentColor),
              hover?.index === index && styles.segmentActive,
            ]}
            aria-hidden
            onPointerEnter={(event: React.PointerEvent<HTMLDivElement>) =>
              show(day, index, event.currentTarget)
            }
          />
        );
      })}
      {hover && (
        <UptimeTooltip
          day={hover.day.day}
          state={dayState(hover.day)}
          downtimeDuration={hover.day.downtimeDuration}
          maintenanceDuration={hover.day.maintenanceDuration}
          anchorX={hover.anchorX}
          viewportTop={hover.viewportTop}
          containerWidth={hover.containerWidth}
        />
      )}
    </html.div>
  );
}

const styles = css.create({
  bar: {
    position: 'relative',
    display: 'flex',
    gap: 2,
    height: 34,
    width: '100%',
  },
  segment: {
    flex: 1,
    borderRadius: 2,
    backgroundColor: color.neutral15,
  },
  segmentColor: (value: string) => ({
    backgroundColor: value,
  }),
  segmentActive: {
    boxShadow: `0 0 0 2px ${color.neutral100}`,
  },
});
